import { unref, type Ref, watch } from 'vue'

type MaybeRef<T> = T | Ref<T>

interface Options {
  root: MaybeRef<HTMLElement | null | undefined>
  offsetY?: number
}

const calculateTranslateY = (elm: HTMLElement, root: HTMLElement, offsetY: number = 0) => {
  if (!window.visualViewport) return 0

  const { top, bottom, height } = elm.getBoundingClientRect()

  const { bottom: rootBottom, height: rootHeight } = root.getBoundingClientRect()

  const positionAbove =
    bottom > window.visualViewport.height && top - height - offsetY * 2 - rootHeight > 0 ? true : false

  return positionAbove
    ? `calc(-100% + ${-(top - rootBottom) - offsetY - rootHeight}px)`
    : `${-(top - rootBottom) + offsetY}px`
}

const calculateTranslateX = (elm: HTMLElement) => {
  if (!window.visualViewport) return 0

  const { left, width } = elm.getBoundingClientRect()

  const isOutside = left + width > window.innerWidth ? true : false

  return isOutside ? `${(left - window.innerWidth + width) * -1}px` : `${0}px`
}

export const useElementRepositioning = (
  element: MaybeRef<HTMLElement | null | undefined>,
  options: MaybeRef<Options>,
) => {
  const reposition = () => {
    const { root, offsetY } = unref(options)

    const elm = unref(element)
    const rootElm = unref(root)

    if (!elm || !rootElm) return

    elm?.style.removeProperty('transform')

    elm?.style.setProperty(
      'transform',
      `translate(${calculateTranslateX(elm)}, ${calculateTranslateY(elm, rootElm, offsetY)})`,
    )
  }

  watch(() => unref(element), reposition)

  const addRepositionListener = () => {
    window.addEventListener('resize', reposition)
    // Scroll does not bubble; capture is needed to catch inner scroll containers.
    window.addEventListener('scroll', reposition, true)
  }

  const removeRepositionListener = () => {
    window.removeEventListener('resize', reposition)
    window.removeEventListener('scroll', reposition, true)
  }

  return {
    repositionElement: reposition,
    addRepositionListener,
    removeRepositionListener,
  }
}
